/**
 * CertificationPackageService — V96-5-1 / V96-5-2 / V96-5-3 / ADR-0045 §6.
 *
 * Builds the three FedRAMP CR26 outputs from live data, then issues them as
 * signed, content-addressed `certification_packages` rows:
 *
 *   - Certification Package Overview (FRR CPO-CSO-OVR / -MTD / -OSA) —
 *     sections follow the rules CPO-CSO-OVR enumerates (CDS-CSO-PUB,
 *     CDS-CSO-SVC, CDS-CSO-IRP, MAS-CSO-IIR/FLO/TPR, CMU-CSO-CMD,
 *     IVV-CSO-ICP). Facts the platform cannot know (FedRAMP ID, UEI, …)
 *     are null and listed in `incomplete_fields` with the rule that needs
 *     them, so the UI can route the owner to fill them in.
 *   - Security Decision Record (FRR SDR-CSO-MTD, SDR-CSX-KSI, SDR-CSX-KMT) —
 *     one entry per KSI applicable to the class, carrying the five
 *     `info.default_artifacts.KSI` items as evidence-derived text, plus a
 *     `stale_statement` flag when validation is older than its cadence
 *     (CR26: an outdated SDR statement is itself a vulnerability).
 *   - Ongoing Certification Report (FRR CCM-OCR-AVL / -NRD) — quarter
 *     summary of KSI status changes, findings, VDR deadlines, accepted
 *     vulnerabilities and significant changes with notice compliance.
 *
 * V103-6-5 / ADR-0068 §5–§7: once the readiness switch names the objective
 * engine, the CPO KSI summary and the SDR KSI entries come from the KSI
 * objective view (no SEC-* crediting, no control_edges read): per-objective
 * determinations, the KSI's program control statement, the FRC-CSX-VVK
 * automated-method count; and an initial Class B/C SDR or CPO is gated on a
 * fresh FedRAMP-recognized assessment (FRC-APP-FIA). The SDR-CSX-KMT metrics
 * and the OCR keep reading `ksi_validation_runs` in both modes.
 *
 * Signing follows ADR-0010 / package-generator: canonical JSON, SHA-256,
 * and an injected `sign` (the hosted signer holds key material). The
 * signature covers {package_id, workspace_id, profile_id, kind,
 * content_sha256} so content can't be replayed under another package or
 * tenant. Verification always recomputes the hash from stored content.
 */
import { getProgramManifest, programHas } from '../programs/manifests.js';
import { SECURITY_BASELINE_CONTROLS } from '../data/security-baseline.js';
import { addTimeframe, getProfile, lookupTimeframe, } from './certification-service.js';
import { computeControlStates, computeFrameworkReadiness } from './requirement-readiness-service.js';
import { queryInChunks } from '../utils/sql-in.js';
import { readFedrampOptions } from '../programs/fedramp-options.js';
import { fedrampCpoFields, missingOfferingFields } from './fedramp-cpo-mapping.js';
import { validateFedrampDocument } from './fedramp-schema-service.js';
import { resolveReadinessEngine } from './program-rule-set-service.js';
import { buildFedrampKsiObjectiveView } from './fedramp-ksi-service.js';
import { ksiProgramControls, objectiveKsiSummary, objectiveSdrParts, decisionFromObjectives } from './fedramp-ksi-packages.js';
import { freshAssessmentGate } from './fedramp-clocks-service.js';
// ---- canonical JSON + hashing -------------------------------------------------
/** Stable key order, no whitespace. Throws on values JSON can't represent faithfully. */
export function canonicalJson(value) {
    return JSON.stringify(canonicalize(value));
}
function canonicalize(v) {
    if (v === null)
        return null;
    if (typeof v === 'number') {
        if (!Number.isFinite(v))
            throw new Error('canonicalJson: non-finite number');
        return v;
    }
    if (typeof v === 'string' || typeof v === 'boolean')
        return v;
    if (v instanceof Date)
        return v.toISOString();
    if (Array.isArray(v))
        return v.map((x) => (x === undefined ? null : canonicalize(x)));
    if (typeof v === 'object') {
        const out = {};
        for (const k of Object.keys(v).sort()) {
            const x = v[k];
            if (x === undefined || typeof x === 'function')
                continue;
            out[k] = canonicalize(x);
        }
        return out;
    }
    throw new Error(`canonicalJson: unsupported ${typeof v}`);
}
export async function sha256Hex(data) {
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data));
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
// ---- shared ----------------------------------------------------------------------
export class CertificationPackageError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.code = code;
        this.name = 'CertificationPackageError';
    }
}
export const CERTIFICATION_PACKAGE_KINDS = [
    'certification_package_overview', 'security_decision_record', 'ongoing_certification_report', 'soc2_pbc_list', 'soc2_risk_assessment',
    'cmmc_ssp', 'cmmc_poam', 'c3pao_package',
    'govramp_ssp_xlsx', 'govramp_poam_xlsx', 'govramp_conmon_bundle', 'govramp_core_submission', 'oscal_ssp', 'fedramp_vdr', 'fedramp_scn',
    'iso_soa',
    'oscal_ar_control', 'oscal_ar_program', 'oscal_poam',
];
/** Kinds only the validated OSCAL export path may write (they need the 672 export record). */
const EXPORT_RECORD_KINDS = new Set(['oscal_ar_control', 'oscal_ar_program', 'oscal_poam']);
const PACKAGE_KINDS = CERTIFICATION_PACKAGE_KINDS;
const MODE = new Map(SECURITY_BASELINE_CONTROLS.map((c) => [c.id, c.mode]));
const CLOSED = ['remediated', 'auto_closed', 'closed'];
const REPORTED_FINDING_KINDS = ['vulnerability', 'ksi_validation_failure'];
/**
 * The five items FedRAMP requires for every KSI (`info.default_artifacts.KSI`,
 * restated in SDR-CSX-KSI). Order matches the source; the test suite pins it
 * against the vendored rules JSON.
 */
export const KSI_DEFAULT_ARTIFACT_FIELDS = [
    'explanation_of_measures',
    'explanation_of_cycle',
    'verification_of_measures',
    'verification_of_automation',
    'validation',
];
async function fedrampProfile(db, workspaceId, profileId) {
    const p = await getProfile(db, workspaceId, profileId);
    if (!p)
        throw new CertificationPackageError('Certification profile not found.', 'not_found');
    if (!programHas(p.program, (m) => m.packages.kinds.includes('certification_package_overview')) || !p.certification_class || !p.path) {
        throw new CertificationPackageError('FedRAMP packages need an active FedRAMP CR26 profile.', 'invalid');
    }
    return p;
}
/** The framework a FedRAMP package reads: the profile's program manifest (ADR-0079), never a hard-coded id. */
function packageFrameworkId(p) {
    const fw = getProgramManifest(p.program)?.frameworkId;
    if (!fw)
        throw new CertificationPackageError('FedRAMP packages need an active FedRAMP CR26 profile.', 'invalid');
    return fw;
}
/** source_version of the most recently approved requirement of the package's framework. */
async function approvedRulesVersion(content, frameworkId) {
    const r = await content.query(`SELECT source_version FROM framework_requirements
      WHERE framework_id = ? AND status = 'active' AND reviewer IS NOT NULL AND source_version IS NOT NULL
      ORDER BY reviewed_at DESC, source_version DESC LIMIT 1`, [frameworkId]);
    const v = r.results[0]?.source_version;
    if (!v) {
        throw new CertificationPackageError('FedRAMP rules are not approved yet — approve the FedRAMP rules batch in the review queue.', 'rules_not_approved');
    }
    return v;
}
const tf = (row) => row ? { rule_id: row.rule_id, timeframe_num: row.timeframe_num, timeframe_type: row.timeframe_type, force: row.force } : null;
async function validationCadences(content, p) {
    const opts = { path: p.path, certificationClass: p.certification_class, pain: null, variant: null };
    return {
        machine: await lookupTimeframe(content, p.path === 'rev5' ? 'VDR-TFR-MVF' : 'VDR-TFR-MVX', opts),
        nonMachine: await lookupTimeframe(content, 'VDR-TFR-NMV', opts),
    };
}
async function latestRuns(db, p, until) {
    const r = await db.query(`SELECT run_id, requirement_id, validation_type, status, completed_at FROM ksi_validation_runs
      WHERE workspace_id = ? AND profile_id = ? ${until ? 'AND completed_at <= ?' : ''}
      ORDER BY completed_at DESC, run_id DESC`, until ? [p.workspace_id, p.profile_id, until] : [p.workspace_id, p.profile_id]);
    const out = new Map();
    for (const row of r.results)
        if (!out.has(row.requirement_id))
            out.set(row.requirement_id, row);
    return out;
}
function dateOnly(iso) {
    return iso ? iso.slice(0, 10) : null;
}
function tfText(row) {
    return `${row.timeframe_num} ${row.timeframe_type === 'bizdays' ? 'business days' : row.timeframe_type}`;
}
/**
 * V103-6-1 / FRC-CSO-JSN: each FedRAMP package carries the FedRAMP schema's
 * fields at its top level (beside GAC Pro's own fields, which the schemas
 * allow) and records the result of validating itself against that schema.
 * A non-conforming package still builds — the gaps are the provider's to
 * fill and are listed in `incomplete_fields` — but it says so.
 */
function withFedrampValidation(type, doc) {
    return { ...doc, fedramp_schema: validateFedrampDocument(type, doc) };
}
/** CCM-OCR-NRD: the last OCR's period end plus the OCR cadence (null until both exist). */
export async function nextOngoingCertificationReportDate(db, content, p) {
    const cadence = await lookupTimeframe(content, 'CCM-OCR-AVL', { path: p.path, certificationClass: p.certification_class, pain: null, variant: null });
    const lastOcr = (await db.query(`SELECT period_end FROM certification_packages
      WHERE workspace_id = ? AND profile_id = ? AND kind = 'ongoing_certification_report' AND period_end IS NOT NULL
      ORDER BY period_end DESC LIMIT 1`, [p.workspace_id, p.profile_id])).results[0]?.period_end ?? null;
    return cadence && lastOcr ? dateOnly(addTimeframe(new Date(lastOcr), cadence.timeframe_num, cadence.timeframe_type).toISOString()) : null;
}
// ---- Certification Package Overview (V96-5-1) -------------------------------------
export async function buildCertificationPackageOverview(db, opts) {
    const content = opts.contentDb ?? db;
    const p = await fedrampProfile(db, opts.workspace_id, opts.profile_id);
    const rulesVersion = await approvedRulesVersion(content, packageFrameworkId(p));
    const nowIso = opts.now.toISOString();
    const ws = (await db.query(`SELECT name, slug, primary_domain FROM workspaces WHERE workspace_id = ? LIMIT 1`, [p.workspace_id])).results[0];
    const systemIds = JSON.parse(p.system_ids_json || '[]');
    // Tenant plane (may be Postgres): chunked under D1's 100-parameter limit, then
    // ORDER BY name, system_id applied across the chunks.
    const systems = (await queryInChunks(db, systemIds, (ph, chunk) => ({
        sql: `SELECT system_id, name, deployment_status FROM ai_systems
          WHERE workspace_id = ? AND system_id IN (${ph})`,
        params: [p.workspace_id, ...chunk],
    }))).sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : a.system_id < b.system_id ? -1 : a.system_id > b.system_id ? 1 : 0));
    // ADR-0068 §4: the objective engine never reads the control graph.
    const objectiveMode = (await resolveReadinessEngine(content, p.program)).engine === 'objective';
    const view = objectiveMode ? await buildFedrampKsiObjectiveView(db, p.workspace_id, p.profile_id, { now: opts.now, contentDb: content }) : null;
    const readiness = view ? null : await computeFrameworkReadiness(db, p.workspace_id, packageFrameworkId(p), {
        certificationClass: p.certification_class, now: opts.now, contentDb: content, profileId: p.profile_id,
    });
    const pcs = view ? await ksiProgramControls(db, p.workspace_id, p.profile_id) : null;
    const runs = [...(await latestRuns(db, p, nowIso)).values()];
    const latest = (type) => runs.filter((r) => !type || r.validation_type === type).map((r) => r.completed_at).sort().pop() ?? null;
    const findingRows = (await db.query(`SELECT kind, COUNT(*) AS open, SUM(CASE WHEN sla_deadline_at < ? THEN 1 ELSE 0 END) AS overdue
       FROM findings WHERE workspace_id = ? AND status IN ('open', 'acknowledged') GROUP BY kind ORDER BY kind`, [nowIso, p.workspace_id])).results;
    const weaknesses = (await db.query(`SELECT COUNT(*) AS n FROM accepted_weaknesses WHERE workspace_id = ? AND status = 'active'`, [p.workspace_id])).results[0]?.n ?? 0;
    // SCN-CSO-HIS: 12 months of significant change history.
    const since = addTimeframe(opts.now, -12, 'months').toISOString();
    const changes = (await db.query(`SELECT classification, COUNT(*) AS n FROM significant_changes
      WHERE workspace_id = ? AND status != 'cancelled' AND COALESCE(completed_at, created_at) >= ? AND created_at <= ?
      GROUP BY classification`, [p.workspace_id, since, nowIso])).results;
    const changeCounts = { routine: 0, adaptive: 0, transformative: 0 };
    for (const c of changes)
        changeCounts[c.classification] = c.n;
    const maintenance = await lookupTimeframe(content, p.path === 'rev5' ? 'CPO-CSF-CPM' : 'CPO-CSX-CPM', {
        path: p.path, certificationClass: p.certification_class, pain: null, variant: null,
    });
    const nextOcr = await nextOngoingCertificationReportDate(db, content, p);
    const o = readFedrampOptions(p.program_options_json).offering ?? {};
    const incomplete = [
        ...missingOfferingFields(o).map((f) => [f.field, f.rule_id]),
        ['service_list[].security_category', 'CDS-CSO-SVC'],
        ['policies', 'CDS-CSO-IRP'],
        ['boundary.information_flows', 'MAS-CSO-FLO'],
        ['boundary.third_party_resources', 'MAS-CSO-TPR'],
        ['cryptographic_modules', 'CMU-CSO-CMD'],
        ['independent_assessment.overall_summary', 'CPO-CSO-OSA'],
    ].map(([field, rule_id]) => ({ field, rule_id }));
    if (systems.length === 0)
        incomplete.push({ field: 'boundary.information_resources', rule_id: 'MAS-CSO-IIR' });
    const programControls = view && pcs ? view.rows.map((r) => ({
        ksi_id: r.ref, requirement_id: r.requirement_id,
        implementation_status: pcs.get(r.requirement_id)?.implementation_status ?? null,
        revision_id: pcs.get(r.requirement_id)?.revision_id ?? null,
    })) : null;
    for (const c of programControls ?? []) {
        if (!c.revision_id)
            incomplete.push({ field: `program_controls[${c.ksi_id}].statement`, rule_id: 'SDR-CSX-KSI', action: 'edit_program_control' });
    }
    const fedramp = fedrampCpoFields(o, {
        provider_fallback: ws?.name ?? null,
        certification_type: p.path === 'rev5' ? 'Rev5' : '20x',
        next_ongoing_certification_report_date: nextOcr,
    });
    return withFedrampValidation('cpo', {
        ...fedramp,
        schema: 'gacpro/fedramp-cpo@1',
        kind: 'certification_package_overview',
        fedramp_rules_version: rulesVersion,
        rules_version: rulesVersion,
        generated_at: nowIso,
        metadata: {
            responsible_official: o.responsible_official ?? null,
            version: rulesVersion,
            last_updated: nowIso,
            source_of_update: 'gacpro: generated from certification profile, control graph and validation runs',
        },
        certification: {
            program: p.program,
            certification_class: p.certification_class,
            path: p.path,
            profile_id: p.profile_id,
            target_date: p.target_date,
            package_maintenance: tf(maintenance),
        },
        offering: {
            name: ws?.name ?? null,
            provider_domain: ws?.primary_domain ?? null,
            fedramp_id: o.fedramp_id ?? null,
            service_model: o.service_models ?? null,
            deployment_model: o.deployment_model ?? null,
            business_category: o.business_categories ?? null,
            uei_number: o.uei_number ?? null,
            sales_contact: o.contacts?.find((c) => c.type.trim().toLowerCase() === 'sales') ?? null,
            security_contact: o.contacts?.find((c) => c.type.trim().toLowerCase() === 'security') ?? null,
            service_description: o.service_description ?? null,
            secure_configuration_guidance_url: o.secure_configuration_guidance?.url ?? null,
            trust_center_url: o.trust_center?.url ?? null,
            independent_assessor: o.assessor ?? null,
            next_ongoing_certification_report_date: nextOcr,
        },
        service_list: systems.map((s) => ({ system_id: s.system_id, name: s.name, deployment_status: s.deployment_status, security_category: null })),
        policies: null,
        boundary: {
            information_resources: systems.map((s) => ({ system_id: s.system_id, name: s.name })),
            information_flows: null,
            third_party_resources: null,
        },
        cryptographic_modules: null,
        independent_assessment: { overall_summary: null },
        ksi_summary: view ? objectiveKsiSummary(view) : {
            met: readiness.counts.met,
            partial: readiness.counts.partial,
            gap: readiness.counts.gap,
            unmapped: readiness.counts.unmapped,
            total: readiness.counts.total,
            readiness_pct: readiness.readiness_pct,
            coverage_pct: readiness.coverage_pct,
        },
        ...(programControls ? { program_controls: programControls } : {}),
        validation: {
            requirements_validated: runs.length,
            last_validation_at: latest(),
            last_machine_validation_at: latest('machine'),
            last_non_machine_validation_at: latest('non_machine'),
        },
        findings: {
            open_by_kind: Object.fromEntries(findingRows.map((f) => [f.kind, f.open])),
            overdue_by_kind: Object.fromEntries(findingRows.map((f) => [f.kind, f.overdue ?? 0])),
        },
        accepted_weaknesses: { active: weaknesses },
        significant_changes_last_12_months: { ...changeCounts, total: changeCounts.routine + changeCounts.adaptive + changeCounts.transformative },
        incomplete_fields: incomplete,
    });
}
const FEDRAMP_STATUS = {
    implemented: 'Implemented', partially_implemented: 'Partially Implemented', not_implemented: 'Not Implemented',
};
const DECISION = { met: 'implemented', partial: 'partially_implemented', gap: 'not_implemented', unmapped: 'not_implemented' };
export async function buildSecurityDecisionRecord(db, opts) {
    const content = opts.contentDb ?? db;
    const p = await fedrampProfile(db, opts.workspace_id, opts.profile_id);
    const rulesVersion = await approvedRulesVersion(content, packageFrameworkId(p));
    const now = opts.now;
    const nowIso = now.toISOString();
    const cls = p.certification_class;
    if ((await resolveReadinessEngine(content, p.program)).engine === 'objective') {
        return buildObjectiveSecurityDecisionRecord(db, content, p, { now, rulesVersion });
    }
    const readiness = await computeFrameworkReadiness(db, p.workspace_id, packageFrameworkId(p), {
        certificationClass: cls, now, contentDb: content, profileId: p.profile_id,
    });
    const reqRows = (await content.query(`SELECT requirement_id, statement, applicability_json FROM framework_requirements WHERE framework_id = ? AND status = 'active'`, [packageFrameworkId(p)])).results;
    const statementOf = new Map(reqRows.map((r) => {
        let byClass = {};
        try {
            byClass = JSON.parse(r.applicability_json || '{}').statements_by_class ?? {};
        }
        catch { /* default */ }
        return [r.requirement_id, byClass[cls] ?? r.statement];
    }));
    const controls = [...new Set(readiness.requirements.flatMap((r) => r.crediting_controls.map((c) => c.control_id)))];
    const evidence = await computeControlStates(db, p.workspace_id, controls, { now, contentDb: content, profileId: p.profile_id });
    const cad = await validationCadences(content, p);
    const last = await latestRuns(db, p, nowIso);
    const d30 = addTimeframe(now, -30, 'days').toISOString();
    const d365 = addTimeframe(now, -1, 'years').toISOString();
    const metricRows = (await db.query(`SELECT requirement_id, completed_at, status FROM ksi_validation_runs
      WHERE workspace_id = ? AND profile_id = ? AND completed_at > ? AND completed_at <= ?`, [p.workspace_id, p.profile_id, d365, nowIso])).results;
    const ksis = readiness.requirements.map((req) => {
        const measures = req.crediting_controls.map((c) => {
            const ev = evidence.get(c.control_id);
            return {
                control_id: c.control_id,
                relation: c.relation,
                evidence_mode: (MODE.get(c.control_id) === 'machine' ? 'machine' : 'attested'),
                state: c.state,
                last_check_id: ev?.last_check_id ?? null,
                last_observed_at: ev?.observed_at ?? null,
            };
        });
        const machine = measures.some((m) => m.evidence_mode === 'machine');
        const cadence = machine ? cad.machine : cad.nonMachine;
        const run = last.get(req.requirement_id) ?? null;
        const nextDue = cadence && run ? addTimeframe(new Date(run.completed_at), cadence.timeframe_num, cadence.timeframe_type) : null;
        let stale_reason = null;
        if (!cadence)
            stale_reason = 'cadence_unavailable';
        else if (!run)
            stale_reason = 'never_validated';
        else if (nextDue <= now)
            stale_reason = 'validation_older_than_cadence';
        const mine = metricRows.filter((m) => m.requirement_id === req.requirement_id);
        const metric = (rows) => ({ runs: rows.length, met: rows.filter((r) => r.status === 'met').length, not_met: rows.filter((r) => r.status !== 'met').length });
        const machineMeasures = measures.filter((m) => m.evidence_mode === 'machine');
        const newestCheck = measures.filter((m) => m.last_observed_at).sort((a, b) => (a.last_observed_at < b.last_observed_at ? 1 : -1))[0];
        const artifacts = {
            explanation_of_measures: measures.length
                ? `Demonstrated by ${measures.length} measure${measures.length === 1 ? '' : 's'}: ${measures.map((m) => `${m.control_id} (${m.evidence_mode}, ${m.state})`).join(', ')}.`
                : 'No measure is mapped to this indicator yet; it is not demonstrated. The reason and resulting customer risk must be documented.',
            explanation_of_cycle: cadence
                ? machine
                    ? `Measures are collected automatically; validated every ${tfText(cadence)} (${cadence.rule_id}, Class ${cls}).`
                    : `Measures are attested; validated every ${tfText(cadence)} (${cadence.rule_id}).`
                : 'Validation cycle unavailable: the FedRAMP validation timeframes are not approved.',
            verification_of_measures: measures.length
                ? `${measures.length} reviewed crediting mapping${measures.length === 1 ? '' : 's'} (${[...new Set(measures.map((m) => m.relation))].join(', ')}) link these measures to ${req.ref}.`
                : 'No reviewed mapping links a measure to this indicator.',
            verification_of_automation: machineMeasures.length
                ? `${machineMeasures.length} of ${measures.length} measures are machine-collected${newestCheck ? `; latest check ${newestCheck.last_check_id} at ${newestCheck.last_observed_at}` : '; no check collected yet'}.`
                : measures.length
                    ? 'Automation not used: all measures are attested and follow the non-machine validation cycle.'
                    : 'No automation: no measures are mapped.',
            validation: run
                ? `${cadence ? `Validated ${machine ? 'automatically' : 'by attestation'} every ${tfText(cadence)}; ` : ''}last validation ${dateOnly(run.completed_at)} ${run.status}${stale_reason === 'validation_older_than_cadence' ? ' (overdue)' : ''}.`
                : 'Not validated yet.',
        };
        const needs = [];
        if (!measures.length)
            needs.push('explanation_of_measures', 'verification_of_measures');
        if (!run)
            needs.push('validation');
        return {
            ksi_id: req.ref,
            requirement_id: req.requirement_id,
            name: req.title,
            theme: req.group_ref,
            statement: statementOf.get(req.requirement_id) ?? req.title,
            decision: DECISION[req.status],
            readiness_status: req.status,
            reason: req.reason,
            measures,
            validation: {
                method: machine ? 'machine' : 'non_machine',
                cadence: tf(cadence),
                last_run: run ? { run_id: run.run_id, status: run.status, completed_at: run.completed_at } : null,
                next_due_at: nextDue ? nextDue.toISOString() : null,
            },
            artifacts,
            metrics: { last_30_days: metric(mine.filter((m) => m.completed_at > d30)), last_365_days: metric(mine) },
            stale_statement: stale_reason !== null,
            stale_reason,
            needs_provider_input: needs,
        };
    });
    const summary = { implemented: 0, partially_implemented: 0, not_implemented: 0, stale_statements: 0, total: ksis.length };
    for (const k of ksis) {
        summary[k.decision]++;
        if (k.stale_statement)
            summary.stale_statements++;
    }
    // FedRAMP SDR schema fields (SDR-CSO-FRR / SDR-CSX-KSI). The five KSI artifacts
    // become implementation (measures, cycle) and validation (verification of
    // measures and automation, validation) statements; ksiAssessment holds only
    // what an independent assessor wrote, which the platform does not record yet.
    const o = readFedrampOptions(p.program_options_json).offering ?? {};
    const updateSource = 'gacpro: control graph evidence and ksi_validation_runs';
    const keySecurityIndicators = ksis.map((k) => ({
        ksiId: k.ksi_id,
        ksiImplementationStatus: FEDRAMP_STATUS[k.decision],
        ksiImplementation: [k.artifacts.explanation_of_measures, k.artifacts.explanation_of_cycle],
        ksiValidation: [k.artifacts.verification_of_measures, k.artifacts.verification_of_automation, k.artifacts.validation],
        ksiAssessment: [],
        ksiTests: k.measures.map((m) => `${m.control_id} (${m.evidence_mode === 'machine' ? 'machine check' : 'attestation'})`),
        ksiEvidence: k.measures.filter((m) => m.last_observed_at).map((m) => ({
            evidenceDescription: `${m.control_id}: latest ${m.evidence_mode === 'machine' ? 'check' : 'attestation'}${m.last_check_id ? ` ${m.last_check_id}` : ''} — ${m.state}.`,
            lastUpdated: dateOnly(m.last_observed_at),
        })),
    }));
    const incomplete = [];
    if (!o.certification_package_overview_url)
        incomplete.push({ field: 'offering.certification_package_overview_url', rule_id: 'FRC-CSO-JSN' });
    // FedRAMP process requirements (FRR) have no implementation record in the platform yet.
    incomplete.push({ field: 'fedRampRequirements', rule_id: 'SDR-CSO-FRR' });
    if (ksis.length)
        incomplete.push({ field: 'keySecurityIndicators[].ksiAssessment', rule_id: 'SDR-CSO-FRR' });
    return withFedrampValidation('sdr', {
        ...(o.certification_package_overview_url ? { certificationPackageOverviewUri: o.certification_package_overview_url } : {}),
        fedRampRequirements: [],
        keySecurityIndicators,
        schema: 'gacpro/fedramp-sdr@1',
        kind: 'security_decision_record',
        fedramp_rules_version: rulesVersion,
        rules_version: rulesVersion,
        generated_at: nowIso,
        metadata: {
            version: rulesVersion,
            last_updated: nowIso,
            source_of_update: updateSource,
            // FedRAMP SDR metadata (SDR-CSO-MTD) names.
            lastUpdated: nowIso,
            updateSource,
        },
        certification: { program: p.program, certification_class: cls, path: p.path, profile_id: p.profile_id },
        summary,
        ksis,
        incomplete_fields: incomplete,
    });
}
/** The SDR from the KSI objective view (ADR-0068 §5–§6); metrics still from the ledger. */
async function buildObjectiveSecurityDecisionRecord(db, content, p, opts) {
    const { now, rulesVersion } = opts;
    const nowIso = now.toISOString();
    const cls = p.certification_class;
    const view = await buildFedrampKsiObjectiveView(db, p.workspace_id, p.profile_id, { now, contentDb: content });
    const pcs = await ksiProgramControls(db, p.workspace_id, p.profile_id);
    const statementRows = (await content.query(`SELECT requirement_id, statement, applicability_json FROM framework_requirements WHERE framework_id = ? AND status = 'active'`, [packageFrameworkId(p)])).results;
    const statementOf = new Map(statementRows.map((r) => {
        let byClass = {};
        try {
            byClass = JSON.parse(r.applicability_json || '{}').statements_by_class ?? {};
        }
        catch { /* default */ }
        return [r.requirement_id, byClass[cls] ?? r.statement];
    }));
    const cad = await validationCadences(content, p);
    const last = await latestRuns(db, p, nowIso);
    const d30 = addTimeframe(now, -30, 'days').toISOString();
    const d365 = addTimeframe(now, -1, 'years').toISOString();
    const metricRows = (await db.query(`SELECT requirement_id, completed_at, status FROM ksi_validation_runs
      WHERE workspace_id = ? AND profile_id = ? AND completed_at > ? AND completed_at <= ?`, [p.workspace_id, p.profile_id, d365, nowIso])).results;
    const incomplete = [];
    const ksis = view.rows.map((row) => {
        const pc = pcs.get(row.requirement_id);
        const run = last.get(row.requirement_id) ?? null;
        const machine = row.objectives.some((o) => o.machine_validated);
        const cadence = machine ? cad.machine : cad.nonMachine;
        const nextDue = cadence && run ? addTimeframe(new Date(run.completed_at), cadence.timeframe_num, cadence.timeframe_type) : null;
        let stale_reason = null;
        if (!cadence)
            stale_reason = 'cadence_unavailable';
        else if (!run)
            stale_reason = 'never_validated';
        else if (nextDue <= now)
            stale_reason = 'validation_older_than_cadence';
        const parts = objectiveSdrParts(row, pc, { cls, machine: cad.machine, nonMachine: cad.nonMachine, lastRun: run });
        const mine = metricRows.filter((m) => m.requirement_id === row.requirement_id);
        const metric = (rows) => ({ runs: rows.length, met: rows.filter((r) => r.status === 'met').length, not_met: rows.filter((r) => r.status !== 'met').length });
        const needs = [];
        if (parts.needs_statement) {
            needs.push('explanation_of_measures');
            incomplete.push({ field: `keySecurityIndicators[${row.ref}].ksiImplementation`, rule_id: 'SDR-CSX-KSI', action: 'edit_program_control' });
        }
        if (!run)
            needs.push('validation');
        return {
            ksi_id: row.ref,
            requirement_id: row.requirement_id,
            name: row.title,
            theme: row.group_ref,
            statement: statementOf.get(row.requirement_id) ?? row.title,
            decision: decisionFromObjectives(row),
            readiness_status: row.status,
            reason: row.status === 'met' ? 'Every KSI objective is met.' : `KSI objectives ${row.status.replace('_', ' ')}.`,
            measures: [],
            validation: {
                method: machine ? 'machine' : 'non_machine',
                cadence: tf(cadence),
                last_run: run ? { run_id: run.run_id, status: run.status, completed_at: run.completed_at } : null,
                next_due_at: nextDue ? nextDue.toISOString() : null,
            },
            artifacts: parts.artifacts,
            metrics: { last_30_days: metric(mine.filter((m) => m.completed_at > d30)), last_365_days: metric(mine) },
            stale_statement: stale_reason !== null,
            stale_reason,
            needs_provider_input: needs,
            objectives: parts.objectives,
            automated_methods: parts.automated_methods,
            program_control: parts.program_control,
            _assessment: parts.assessment,
            _tests: parts.tests,
        };
    });
    const summary = { implemented: 0, partially_implemented: 0, not_implemented: 0, stale_statements: 0, total: ksis.length };
    for (const k of ksis) {
        summary[k.decision]++;
        if (k.stale_statement)
            summary.stale_statements++;
    }
    const o = readFedrampOptions(p.program_options_json).offering ?? {};
    const updateSource = 'gacpro: KSI objective determinations, program control statements and ksi_validation_runs';
    const keySecurityIndicators = ksis.map((k) => ({
        ksiId: k.ksi_id,
        ksiImplementationStatus: FEDRAMP_STATUS[k.decision],
        ksiImplementation: [k.artifacts.explanation_of_measures, k.artifacts.explanation_of_cycle],
        ksiValidation: [k.artifacts.verification_of_measures, k.artifacts.verification_of_automation, k.artifacts.validation],
        ksiAssessment: k._assessment,
        ksiTests: k._tests,
        ksiEvidence: (k.objectives ?? []).flatMap((ob) => ob.determinations.map((d) => ({
            evidenceDescription: `${ob.label}: ${d.kind} determination ${d.result.replace('_', ' ')}.`,
            lastUpdated: dateOnly(d.at),
        }))),
    }));
    for (const k of ksis) {
        delete k._assessment;
        delete k._tests;
    }
    if (!o.certification_package_overview_url)
        incomplete.push({ field: 'offering.certification_package_overview_url', rule_id: 'FRC-CSO-JSN' });
    incomplete.push({ field: 'fedRampRequirements', rule_id: 'SDR-CSO-FRR' });
    if (keySecurityIndicators.some((k) => !k.ksiAssessment.length))
        incomplete.push({ field: 'keySecurityIndicators[].ksiAssessment', rule_id: 'SDR-CSO-FRR' });
    return withFedrampValidation('sdr', {
        ...(o.certification_package_overview_url ? { certificationPackageOverviewUri: o.certification_package_overview_url } : {}),
        fedRampRequirements: [],
        keySecurityIndicators,
        schema: 'gacpro/fedramp-sdr@1',
        kind: 'security_decision_record',
        fedramp_rules_version: rulesVersion,
        rules_version: rulesVersion,
        generated_at: nowIso,
        metadata: { version: rulesVersion, last_updated: nowIso, source_of_update: updateSource, lastUpdated: nowIso, updateSource },
        certification: { program: p.program, certification_class: cls, path: p.path, profile_id: p.profile_id },
        readiness_engine: { engine: 'objective', rule_set_id: view.engine.rule_set_id, period_id: view.period?.period_id ?? null },
        automated_validation: view.automated_validation,
        // A frozen package records the program-control revisions it rendered (ADR-0065 §1).
        program_control_revisions: Object.fromEntries([...pcs].filter(([, v]) => v.revision_id).map(([k, v]) => [k, v.revision_id])),
        summary,
        ksis,
        incomplete_fields: incomplete,
    });
}
// ---- Ongoing Certification Report (V96-5-3) ---------------------------------------
export async function buildOngoingCertificationReport(db, opts) {
    const content = opts.contentDb ?? db;
    const p = await fedrampProfile(db, opts.workspace_id, opts.profile_id);
    const rulesVersion = await approvedRulesVersion(content, packageFrameworkId(p));
    const start = Date.parse(opts.period_start);
    const end = Date.parse(opts.period_end);
    if (Number.isNaN(start) || Number.isNaN(end) || start >= end) {
        throw new CertificationPackageError('period_start must be a date before period_end.', 'invalid');
    }
    const ps = new Date(start).toISOString();
    const pe = new Date(end).toISOString();
    const tfOpts = { path: p.path, certificationClass: p.certification_class, pain: null, variant: null };
    const cadence = await lookupTimeframe(content, 'CCM-OCR-AVL', tfOpts);
    // KSI status changes: baseline = last run before the period, then each run in it.
    const baseline = await latestRuns(db, p, new Date(start - 1).toISOString());
    const periodRuns = (await db.query(`SELECT run_id, requirement_id, validation_type, status, completed_at FROM ksi_validation_runs
      WHERE workspace_id = ? AND profile_id = ? AND completed_at >= ? AND completed_at <= ?
      ORDER BY completed_at, run_id`, [p.workspace_id, p.profile_id, ps, pe])).results;
    const current = new Map([...baseline].map(([k, v]) => [k, v.status]));
    const statusChanges = [];
    for (const r of periodRuns) {
        const prev = current.get(r.requirement_id) ?? null;
        if (prev !== r.status) {
            statusChanges.push({ ksi_id: r.requirement_id.split(':').pop(), requirement_id: r.requirement_id, from: prev, to: r.status, at: r.completed_at });
        }
        current.set(r.requirement_id, r.status);
    }
    const endStatus = { met: 0, not_met: 0 };
    for (const s of current.values()) {
        if (s === 'met')
            endStatus.met++;
        else
            endStatus.not_met++;
    }
    // Findings (vulnerability + ksi_validation_failure) as of period end.
    const kinds = [...REPORTED_FINDING_KINDS]; // d1-in-list: bounded — static constant
    const rows = (await db.query(`SELECT finding_id, kind, status, opened_at, closed_at, sla_deadline_at, deadline_policy FROM findings
      WHERE workspace_id = ? AND kind IN (${kinds.map(() => '?').join(',')}) AND opened_at <= ?`, [p.workspace_id, ...kinds, pe])).results;
    const closedBy = (f, at) => CLOSED.includes(f.status) && f.closed_at != null && f.closed_at <= at;
    const findings = {};
    for (const k of kinds)
        findings[k] = { opened: 0, closed: 0, open_at_period_end: 0, overdue_at_period_end: 0 };
    const vdr = { due_in_period: 0, met: 0, missed: 0 };
    for (const f of rows) {
        const b = findings[f.kind];
        if (f.opened_at >= ps)
            b.opened++;
        if (closedBy(f, pe) && f.closed_at >= ps)
            b.closed++;
        if (!closedBy(f, pe)) {
            b.open_at_period_end++;
            if (f.sla_deadline_at < pe)
                b.overdue_at_period_end++;
        }
        if (f.deadline_policy === 'vdr' && f.sla_deadline_at >= ps && f.sla_deadline_at <= pe) {
            vdr.due_in_period++;
            if (closedBy(f, f.sla_deadline_at))
                vdr.met++;
            else
                vdr.missed++;
        }
    }
    // Accepted vulnerabilities active at any point in the period. Justification
    // text is withheld (CCM-OCR-LSI: no sensitive detail in the report).
    const accepted = (await db.query(`SELECT weakness_id, finding_id, classification, accepted_at, review_due_at, status FROM accepted_weaknesses
      WHERE workspace_id = ? AND accepted_at <= ? AND (status = 'active' OR (status = 'revoked' AND revoked_at >= ?) OR (status = 'expired' AND review_due_at >= ?))
      ORDER BY accepted_at, weakness_id`, [p.workspace_id, pe, ps, ps])).results;
    // Significant changes touching the period, with notice compliance.
    const changeRows = (await db.query(`SELECT change_id, title, classification, status, planned_start, completed_at, verified_at, notices_json, created_at
       FROM significant_changes
      WHERE workspace_id = ? AND created_at <= ?
        AND (created_at >= ? OR completed_at >= ? OR verified_at >= ? OR status IN ('planned', 'in_progress'))
      ORDER BY created_at, change_id`, [p.workspace_id, pe, ps, ps, ps])).results;
    const complianceOf = (n) => {
        if (n.status === 'blocked_rules_not_approved')
            return 'blocked_rules_not_approved';
        if (n.sent_at)
            return n.due_at && n.sent_at > n.due_at ? 'late' : 'on_time';
        if (n.status === 'cancelled')
            return 'cancelled';
        if (n.due_at && n.due_at < pe)
            return 'missed';
        return 'pending';
    };
    const changes = changeRows.map((c) => ({
        change_id: c.change_id,
        title: c.title,
        classification: c.classification,
        status: c.status,
        planned_start: c.planned_start,
        completed_at: c.completed_at,
        verified_at: c.verified_at,
        notices: JSON.parse(c.notices_json || '[]').map((n) => ({
            rule_id: n.rule_id, due_at: n.due_at, sent_at: n.sent_at, compliance: complianceOf(n),
        })),
    }));
    const horizon = cadence ? addTimeframe(new Date(end), cadence.timeframe_num, cadence.timeframe_type).toISOString() : null;
    const planned = changes.filter((c) => (c.status === 'planned' || c.status === 'in_progress') && c.planned_start && c.planned_start > pe && (!horizon || c.planned_start <= horizon));
    const inPeriod = changes.filter((c) => !planned.includes(c));
    // Agencies that confirmed on their CDS grant that they use the offering during the period.
    const confirmed = (await db.query(`SELECT DISTINCT agency_name FROM cds_grants
      WHERE workspace_id = ? AND profile_id = ? AND usage_status = 'confirmed'
        AND created_at <= ? AND expires_at >= ? AND (revoked_at IS NULL OR revoked_at >= ?)`, [p.workspace_id, p.profile_id, pe, ps, ps])).results.map((r) => r.agency_name);
    // FedRAMP OCR schema fields (CCM-OCR-AVL). Summaries are built from the
    // records above; incidents and recommendations come only from the provider
    // (an empty incidents array is an attestation that none occurred).
    const o = readFedrampOptions(p.program_options_json).offering ?? {};
    const input = opts.provider_input ?? {};
    const CLASS = (c) => c.charAt(0).toUpperCase() + c.slice(1);
    const changeLine = (c) => `${CLASS(c.classification)} change "${c.title}" (${c.status.replace('_', ' ')})`;
    const certificationDataChanges = [
        ...inPeriod.map(changeLine),
        ...(statusChanges.length ? [`${statusChanges.length} KSI status change${statusChanges.length === 1 ? '' : 's'} recorded by validation in the period.`] : []),
    ];
    const acceptedSummary = accepted.length
        ? `${accepted.length} accepted vulnerabilit${accepted.length === 1 ? 'y' : 'ies'} active during the period (${Object.entries(accepted.reduce((m, a) => ({ ...m, [a.classification]: (m[a.classification] ?? 0) + 1 }), {}))
            .sort(([a], [b]) => (a < b ? -1 : 1)).map(([k, n]) => `${k.replace(/_/g, ' ')}: ${n}`).join(', ')}). Full records are reported per VER-RPT-AVI.`
        : 'No accepted vulnerabilities were active during the period.';
    const fedramp = {
        ...(o.certification_package_overview_url ? { certificationPackageOverviewUri: o.certification_package_overview_url } : {}),
        reportPeriod: { from: dateOnly(ps), to: dateOnly(pe) },
        certificationDataChanges,
        ...(horizon ? {
            plannedCertificationDataChanges: {
                planningHorizonThrough: dateOnly(horizon),
                changes: planned.map((c) => `${changeLine(c)} planned to start ${dateOnly(c.planned_start)}`),
            },
        } : {}),
        acceptedVulnerabilities: acceptedSummary,
        transformativeChanges: inPeriod.filter((c) => c.classification === 'transformative').map(changeLine),
        activeAgencies: [...new Set([...(input.active_agencies ?? []), ...confirmed])].sort(),
        ...(input.updated_recommendations ? { updatedRecommendations: input.updated_recommendations } : {}),
        ...(input.reportable_incidents ? {
            reportableIncidents: {
                incidents: input.reportable_incidents.map((i) => ({
                    summary: i.summary,
                    ...(i.occurred_at ? { occurredAt: i.occurred_at } : {}),
                    ...(i.resolved_at ? { resolvedAt: i.resolved_at } : {}),
                    ...(i.lessons_learned?.length ? { incidentLessonsLearned: i.lessons_learned } : {}),
                })),
            },
        } : {}),
    };
    const incomplete = [];
    if (!input.active_agencies)
        incomplete.push({ field: 'provider_input.agencies_using_product', rule_id: 'CCM-OCR-AVL' });
    if (!input.reportable_incidents)
        incomplete.push({ field: 'provider_input.reportable_incidents', rule_id: 'CCM-OCR-AVL' });
    if (!input.updated_recommendations)
        incomplete.push({ field: 'provider_input.updated_recommendations', rule_id: 'CCM-OCR-AVL' });
    if (!o.certification_package_overview_url)
        incomplete.push({ field: 'offering.certification_package_overview_url', rule_id: 'FRC-CSO-JSN' });
    if (!horizon)
        incomplete.push({ field: 'plannedCertificationDataChanges', rule_id: 'CCM-OCR-AVL' });
    return withFedrampValidation('ocr', {
        ...fedramp,
        schema: 'gacpro/fedramp-ocr@1',
        kind: 'ongoing_certification_report',
        fedramp_rules_version: rulesVersion,
        rules_version: rulesVersion,
        generated_at: (opts.now ? opts.now.toISOString() : pe),
        period: { start: ps, end: pe },
        certification: { program: p.program, certification_class: p.certification_class, path: p.path, profile_id: p.profile_id },
        reporting: {
            cadence: tf(cadence),
            next_report_target_date: horizon ? dateOnly(horizon) : null,
        },
        ksi_status: {
            changes: statusChanges,
            at_period_end: endStatus,
            validations_in_period: periodRuns.length,
        },
        findings,
        vdr_deadlines: vdr,
        accepted_vulnerabilities: accepted,
        significant_changes: {
            in_period: inPeriod,
            transformative: changes.filter((c) => c.classification === 'transformative').map((c) => c.change_id),
            planned_next_period: planned,
            notices_missed: changes.flatMap((c) => c.notices).filter((n) => n.compliance === 'missed').length,
            notices_late: changes.flatMap((c) => c.notices).filter((n) => n.compliance === 'late').length,
        },
        provider_input: {
            agencies_using_product: input.active_agencies ?? null,
            reportable_incidents: input.reportable_incidents ?? null,
            lessons_learned: input.reportable_incidents ? input.reportable_incidents.flatMap((i) => i.lessons_learned ?? []) : null,
            updated_recommendations: input.updated_recommendations ?? null,
        },
        incomplete_fields: incomplete,
    });
}
/** What the signature covers: binds the content hash to this package + tenant. */
function signingPayload(pkg) {
    return canonicalJson({
        content_sha256: pkg.content_sha256,
        kind: pkg.kind,
        package_id: pkg.package_id,
        profile_id: pkg.profile_id,
        schema: 'gacpro/certification-package-signature@1',
        workspace_id: pkg.workspace_id,
    });
}
/** V109-6 security L2: the digest of an export record (every column the export lists or relies on). */
export async function exportRecordDigest(r) {
    return sha256Hex(canonicalJson({
        content_r2_key: r.content_r2_key ?? null, period_end: r.period_end ?? null, period_start: r.period_start ?? null,
        schema: 'gacpro/oscal-export-record@1', schema_version: r.schema_version ?? null, scope_json: r.scope_json ?? null,
        source_run_id: r.source_run_id ?? null, validation_json: r.validation_json ?? null,
    }));
}
/**
 * V109-6 security L2: the OSCAL export kinds sign `…signature@2` — the @1 fields plus the export
 * record's digest, so the record (schema, sweep, scope, validation, period, storage key) can't be
 * changed without breaking the signature. Every other kind keeps @1.
 */
export function signingPayloadV2(pkg) {
    return canonicalJson({
        content_sha256: pkg.content_sha256,
        kind: pkg.kind,
        package_id: pkg.package_id,
        profile_id: pkg.profile_id,
        record_sha256: pkg.record_sha256,
        schema: 'gacpro/certification-package-signature@2',
        workspace_id: pkg.workspace_id,
    });
}
/** The kinds signed with `…signature@2` (the validated OSCAL exports, migration 673). */
export const SIGNATURE_V2_KINDS = new Set(['oscal_ar_control', 'oscal_ar_program', 'oscal_poam']);
export async function issueCertificationPackage(db, input) {
    if (!PACKAGE_KINDS.includes(input.kind))
        throw new CertificationPackageError(`Unknown package kind ${String(input.kind)}.`, 'invalid');
    if (EXPORT_RECORD_KINDS.has(input.kind))
        throw new CertificationPackageError(`${input.kind} is issued only through the validated OSCAL export path.`, 'invalid');
    const profile = await getProfile(db, input.workspace_id, input.profile_id);
    if (!profile)
        throw new CertificationPackageError('Certification profile not found.', 'not_found');
    // ADR-0068 §7 / FRC-APP-FIA: an initial Class B/C SDR or CPO needs a fresh
    // FedRAMP-recognized assessment of every in-scope KSI objective (objective engine).
    if ((input.kind === 'security_decision_record' || input.kind === 'certification_package_overview')
        && programHas(profile.program, (m) => !!m.features.persistentValidation)
        && (await resolveReadinessEngine(input.contentDb ?? db, profile.program)).engine === 'objective') {
        const gate = await freshAssessmentGate(db, profile, input.now ?? new Date(), { contentDb: input.contentDb });
        if (gate.required && gate.status !== 'met') {
            throw new CertificationPackageError(gate.status === 'awaiting_assessor' || gate.status === 'no_period'
                ? 'Awaiting assessor: the first FedRAMP package needs a fresh independent assessment by a FedRAMP Recognized assessor (FRC-APP-FIA).'
                : `The fresh independent assessment does not cover every KSI objective within ${gate.provenance.rule_id} (${gate.missing.length} missing or older than the timeframe).`, 'fresh_assessment_required');
        }
    }
    if (input.kind === 'ongoing_certification_report' && (!input.period_start || !input.period_end)) {
        throw new CertificationPackageError('An Ongoing Certification Report needs period_start and period_end.', 'invalid');
    }
    const content_json = canonicalJson(input.content);
    const content_sha256 = await sha256Hex(content_json);
    const base = { package_id: input.package_id, workspace_id: input.workspace_id, profile_id: input.profile_id, kind: input.kind, content_sha256 };
    const { key_id, signature } = await input.sign(input.workspace_id, signingPayload(base));
    // The signer must use this workspace's key — never another tenant's.
    const key = (await db.query(`SELECT workspace_id FROM hmac_keys WHERE key_id = ? AND workspace_id = ? LIMIT 1`, [key_id, input.workspace_id])).results[0];
    if (!key || key.workspace_id !== input.workspace_id) {
        throw new CertificationPackageError('Signing key does not belong to this workspace.', 'invalid');
    }
    const rulesVersion = input.rules_version ? input.rules_version
        : typeof input.content?.fedramp_rules_version === 'string'
            ? input.content.fedramp_rules_version
            : profile.rules_version;
    await db.execute(`INSERT INTO certification_packages (
       package_id, workspace_id, profile_id, kind, period_start, period_end, rules_version,
       content_json, content_sha256, hmac_key_id, hmac_signature, created_by_user_id, created_at
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [
        input.package_id, input.workspace_id, input.profile_id, input.kind, input.period_start ?? null, input.period_end ?? null,
        rulesVersion, content_json, content_sha256, key_id, signature, input.created_by_user_id ?? null,
        (input.now ?? new Date()).toISOString(),
    ]);
    return (await getCertificationPackage(db, input.workspace_id, input.package_id));
}
export async function verifyCertificationPackage(db, packageId, verify, opts = {}) {
    const r = await db.query(`SELECT * FROM certification_packages WHERE package_id = ? ${opts.workspace_id ? 'AND workspace_id = ?' : ''} LIMIT 1`, opts.workspace_id ? [packageId, opts.workspace_id] : [packageId]);
    const pkg = r.results[0];
    if (!pkg)
        return { valid: false, content_sha256: null, stored_sha256_matches: false, package_id: packageId, kind: null, signed_content_sha256: null, issued_at: null };
    // M4: a large OSCAL export keeps its content in object storage; the row keeps the digest.
    let content = pkg.content_json;
    if (pkg.content_r2_key) {
        content = null;
        if (opts.readObject && pkg.content_r2_key.startsWith(`certification-packages/${pkg.workspace_id}/`)) {
            const bytes = await opts.readObject(pkg.content_r2_key).catch(() => null);
            content = bytes ? new TextDecoder().decode(bytes) : null;
        }
    }
    const recomputed = content === null ? null : await sha256Hex(content);
    const matches = recomputed !== null && recomputed === pkg.content_sha256;
    let sigOk = false;
    try {
        // L2: the OSCAL export kinds are signed over the content AND the export record (@2).
        const payload = SIGNATURE_V2_KINDS.has(pkg.kind)
            ? signingPayloadV2({ ...pkg, content_sha256: recomputed ?? '', record_sha256: await exportRecordDigest({
                    schema_version: pkg.schema_version ?? null, source_run_id: pkg.source_run_id ?? null, scope_json: pkg.scope_json ?? null,
                    validation_json: pkg.validation_json ?? null, period_start: pkg.period_start, period_end: pkg.period_end, content_r2_key: pkg.content_r2_key ?? null,
                }) })
            : signingPayload({ ...pkg, content_sha256: recomputed ?? '' });
        sigOk = recomputed !== null && await verify(pkg.workspace_id, payload, pkg.hmac_key_id, pkg.hmac_signature);
    }
    catch {
        sigOk = false;
    }
    return {
        valid: matches && sigOk, content_sha256: recomputed, stored_sha256_matches: matches, package_id: pkg.package_id, kind: pkg.kind,
        signed_content_sha256: pkg.content_sha256, issued_at: pkg.created_at, content_json: matches ? content : null,
    };
}
export async function getCertificationPackage(db, workspaceId, packageId) {
    const r = await db.query(`SELECT * FROM certification_packages WHERE package_id = ? AND workspace_id = ? LIMIT 1`, [packageId, workspaceId]);
    return r.results[0] ?? null;
}
export async function listCertificationPackages(db, workspaceId, profileId) {
    const r = await db.query(`SELECT package_id, workspace_id, profile_id, kind, period_start, period_end, rules_version, content_sha256,
            hmac_key_id, hmac_signature, created_by_user_id, created_at
       FROM certification_packages
      WHERE workspace_id = ? ${profileId ? 'AND profile_id = ?' : ''}
      ORDER BY created_at DESC, package_id`, profileId ? [workspaceId, profileId] : [workspaceId]);
    return r.results;
}
//# sourceMappingURL=certification-package-service.js.map